import { useMemo } from 'react';

import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import PageProgress from 'components/progress/PageProgress';
import { getUserSafePlayStatus } from 'components/safePlay/utils';
import { Feature, featureEnabled } from 'config/clients';
import { getSafePlayRouteValue, useCheckSamePath } from 'config/router/utils';
import { useLevelConfig } from 'hooks/useLevelConfiguration';
import { useUserManagementContext } from 'hooks/userManagement/useUserManagementContext';
import { SafePlayStatus } from 'typedefs/authTypes';

const SafePlayOutlet = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { user, loading: userLoading } = useUserManagementContext();
  const isSamePath = useCheckSamePath();
  const location = useLocation();
  const levelConfig = useLevelConfig(tournamentId);
  const userSafePlayStatus = getUserSafePlayStatus(user);
  const isApproved = userSafePlayStatus === SafePlayStatus.Approved;
  const safePlayRoute = getSafePlayRouteValue(userSafePlayStatus);
  const loading = userLoading || levelConfig.requireSafeplayApproval === undefined;
  const requireSafeplayApproval = levelConfig.requireSafeplayApproval ?? true;

  const isDenied = useMemo(() => {
    return requireSafeplayApproval && featureEnabled(Feature.SAFE_PLAY) && !isApproved && !isSamePath(safePlayRoute);
  }, [isApproved, isSamePath, requireSafeplayApproval, safePlayRoute]);

  if (loading) return <PageProgress />;

  if (isDenied) {
    return <Navigate replace state={{ from: location }} to={safePlayRoute} />;
  }

  return <Outlet />;
};

export default SafePlayOutlet;
